import type { FlagSeverity, DisciplineFlag } from './api';

// ─────────────────────────────────────────────────────────────────────────────
// disciplineFlagDisplay — shared severity/status presentation for discipline
// flags, factored out of DisciplineFlagPanel so the dedicated /admin/discipline
// page (DisciplinePage.tsx) can render identical severity chips + status
// badges without duplicating (and risking drift from) the panel's styling.
//
// Single authority for "what a severity/status looks like" — DisciplineFlagPanel
// and DisciplinePage both import from here. Pure presentation, no state/hooks.
// ─────────────────────────────────────────────────────────────────────────────

export const SEVERITY_ORDER: FlagSeverity[] = ['blue', 'yellow', 'red', 'black'];

export type SeverityMeta = {
  label: string;
  swatch: string;
  text: string;
  border: string;
  softBg: string;
};

export const SEVERITY_META: Record<FlagSeverity, SeverityMeta> = {
  blue: {
    label: 'Blue · Caution',
    swatch: 'bg-sky-500',
    text: 'text-sky-300',
    border: 'border-sky-500/40',
    softBg: 'bg-sky-500/10',
  },
  yellow: {
    label: 'Yellow · Warning',
    swatch: 'bg-amber-400',
    text: 'text-amber-300',
    border: 'border-amber-500/40',
    softBg: 'bg-amber-500/10',
  },
  red: {
    label: 'Red · Serious',
    swatch: 'bg-red-500',
    text: 'text-red-300',
    border: 'border-red-500/40',
    softBg: 'bg-red-500/10',
  },
  black: {
    label: 'Black · Severe',
    swatch: 'bg-black ring-1 ring-inset ring-iron-400/50',
    text: 'text-iron-200',
    border: 'border-iron-400/40',
    softBg: 'bg-iron-900',
  },
};

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const URL_RE = /^https?:\/\//i;

export function StatusChip({ status }: { status: DisciplineFlag['status'] }) {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.16em] text-amber-300">
        Awaiting co-sign
      </span>
    );
  }
  if (status === 'overturned') {
    return (
      <span className="inline-flex items-center gap-1 border border-iron-700 bg-iron-950 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.16em] text-iron-500">
        Overturned
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.16em] text-emerald-300">
      Active
    </span>
  );
}
